import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'de' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  de: {
    // Navigation
    'nav.stories': 'Stories',
    'nav.regions': 'REGIONEN',
    'nav.guides': 'GUIDES',
    'nav.about': 'ÜBER UNS',
    'nav.home': 'Startseite',
    'nav.newsletter': 'Newsletter',
    'nav.subscribe': 'Abonnieren',
    'nav.media': 'Mediadaten',
    'nav.contact': 'Kontakt',
    'nav.magazine': 'Magazin',
    
    // Common
    'home': 'Startseite',
    'contact': 'Kontakt',
    'newsletter': 'Newsletter',
    'media.data': 'Mediadaten',
    'subscribe': 'Abonnieren',
    'privacy': 'Datenschutz',
    'imprint': 'Impressum',
    'terms': 'AGB',
    'back': 'Zurück',
    'phone': 'Telefon',
    'email': 'E-Mail',
    'message': 'Nachricht',
    'send': 'Senden',
    'sending': 'Wird gesendet...',
    'required': 'Pflichtfeld',
    
    // Newsletter Page
    'newsletter.page.title': 'Newsletter Anmeldung',
    'newsletter.page.description': 'Erhalten Sie die neuesten Updates zu den schönsten Langlaufgebieten der Schweiz, Streckenbedingungen und exklusive Insider-Tipps direkt in Ihr Postfach.',
    'newsletter.signup.title': 'Melden Sie sich jetzt für unseren Newsletter an',
    'newsletter.form.salutation': 'Anrede',
    'newsletter.form.salutation.mr': 'Herr',
    'newsletter.form.salutation.ms': 'Frau',
    'newsletter.form.salutation.diverse': 'Divers',
    'newsletter.form.firstname': 'Vorname',
    'newsletter.form.lastname': 'Nachname',
    'newsletter.form.email': 'E-Mail *',
    'newsletter.form.privacy': 'Ich akzeptiere die',
    'newsletter.form.privacy.link': 'Datenschutzerklärung',
    'newsletter.form.submit': 'Für Newsletter anmelden',
    'newsletter.form.submitting': 'Wird angemeldet...',
    'newsletter.success.message': 'Vielen Dank! Sie erhalten bald Updates zum Langlauf-Magazin.',
    
    // Contact Page
    'contact.page.title': 'Kontakt',
    'contact.page.description': 'Haben Sie Fragen zum Schweizer Langlauf-Magazin oder möchten Sie mit uns in Kontakt treten? Wir freuen uns auf Ihre Nachricht.',
    'contact.form.name': 'Name *',
    'contact.form.company': 'Firma/Organisation',
    'contact.form.subject': 'Betreff',
    'contact.form.subject.general': 'Allgemeine Anfrage',
    'contact.form.subject.editorial': 'Redaktion',
    'contact.form.subject.advertising': 'Werbung/Marketing',
    'contact.form.subject.subscription': 'Abonnement',
    'contact.form.subject.support': 'Technischer Support',
    'contact.form.message': 'Ihre Nachricht *',
    'contact.form.submit': 'Nachricht senden',
    'contact.form.submitting': 'Wird gesendet...',
    'contact.success.title': 'Nachricht gesendet!',
    'contact.success.message': 'Vielen Dank für Ihre Nachricht. Wir melden uns bald bei Ihnen.',
    'contact.error.title': 'Fehler beim Senden',
    'contact.error.message': 'Bitte versuchen Sie es später erneut.',
    'contact.error.incomplete': 'Bitte füllen Sie alle Pflichtfelder aus.',
    
    // Media Data Page
    'media.page.title': 'Mediadaten',
    'media.page.description': 'Fordern Sie die aktuellen Mediadaten an. Nach Eingabe Ihrer Kontaktdaten wird das PDF freigeschaltet.',
    'media.page.subtitle': 'Zielgruppe und Reichweite',
    'media.arrow.text': 'Entdecken Sie unsere Zielgruppe & Reichweite',
    'media.target.section.title': 'Unsere Zielgruppe & Reichweite', 
    'media.target.section.description': 'Erreichen Sie eine engagierte Community von Langlauf-Enthusiasten und Outdoor-Liebhabern',
    'media.target.audience': 'Zielgruppe',
    'media.circulation.distribution': 'Verbreitung & Auflage',
    'media.age.years': 'Jahre',
    'media.sporty.active': 'Sportlich',
    'media.active': 'Aktiv', 
    'media.nature.connected': 'Natur-',
    'media.connected': 'verbunden',
    'media.income.level': 'Mittleres bis',
    'media.income.high': 'hohes Einkommen',
    'media.circulation.per.issue': 'Auflage pro Ausgabe',
    'media.printed.copies': 'Gedruckte Exemplare',
    'media.subscribers': 'Abonnenten',
    'media.loyal.readership': 'Treue Leserschaft', 
    'media.newsletter.recipients': 'Newsletter',
    'media.recipients': 'Empfänger',
    'media.publishing': 'Erscheinung',
    'media.quarterly': '4× jährlich',
    'media.october': 'Oktober',
    'media.december': 'Dezember', 
    'media.february': 'Februar',
    'media.july': 'Juli',
    'media.distribution': 'Verteilung',
    'media.nationwide': 'Schweizweit',
    'media.focus.hotspots': 'Fokus auf Langlauf-Hotspots',
    'media.giveaway': 'Give-away',
    'media.centers.shops': 'Langlaufzentren & Sportgeschäfte',
    'media.facts.title': 'Fakten zum Langlaufen in der Schweiz',
    'media.facts.trails': 'Präparierte Loipen',
    'media.facts.organizations': '160 Organisationen',
    'media.facts.marathon': 'Engadin Skimarathon',
    'media.facts.participants': 'Teilnehmer jährlich',
    'media.facts.swiss.sport': 'Der Schweizer treiben',
    'media.facts.regular.sport': 'regelmässig Sport',
    'media.form.company': 'Firma',
    'media.form.firstname': 'Vorname *',
    'media.form.lastname': 'Nachname *',
    'media.form.purpose': 'Zweck / Kommentar',
    'media.form.privacy.accept': 'Ich habe die',
    'media.form.privacy.read': 'gelesen und bin einverstanden.',
    'media.form.submit': 'PDF freischalten',
    'media.form.submitting': 'Sende…',
    'media.pdf.title': 'Mediadaten PDF',
    'media.pdf.download': 'Download',
    'media.pdf.blocked': 'Einbettung wurde vom Browser blockiert. Bitte nutzen Sie den Download-Button oben.',
    
    // Subscribe Page
    'subscribe.page.title': 'Wähle dein glide Abo',
    'subscribe.form.firstname': 'Vorname *',
    'subscribe.form.lastname': 'Nachname *',
    'subscribe.form.address': 'Strasse *',
    'subscribe.form.zip': 'PLZ *',
    'subscribe.form.city': 'Ort *',
    'subscribe.form.gift.option': 'Als Geschenk bestellen',
    'subscribe.form.gift.description': 'Das Magazin wird an eine andere Adresse geliefert',
    'subscribe.form.gift.address': 'Geschenk-Lieferadresse',
    'subscribe.form.gift.salutation': 'Anrede',
    'subscribe.form.gift.choose': 'Bitte wählen',
    'subscribe.form.gift.mr': 'Herr',
    'subscribe.form.gift.ms': 'Frau',
    'subscribe.form.subscription': 'Wähle dein Abo',
    'subscribe.form.yearly': 'Jahresabo',
    'subscribe.form.yearly.desc': '4 Ausgaben für CHF 49.-',
    'subscribe.form.twoyear': '2-Jahresabo',
    'subscribe.form.twoyear.desc': '8 Ausgaben für CHF 78.-',
    'subscribe.form.message.optional': 'Nachricht (optional)',
    'subscribe.form.message.placeholder': 'Ihre Nachricht...',
    'subscribe.form.terms.accept': 'Ich akzeptiere die',
    'subscribe.form.terms.link': 'AGB',
    'subscribe.form.submit': 'Abonnement bestellen',
    'subscribe.form.submitting': 'Wird bestellt...',
    'subscribe.success.title': 'Bestellung erfolgreich!',
    'subscribe.success.message': 'Ihre Magazin-Bestellung wurde erfolgreich übermittelt. Sie erhalten eine Bestätigung per E-Mail.',
    'subscribe.error.terms': 'Bitte akzeptieren Sie die Allgemeinen Geschäftsbedingungen.',
    'subscribe.error.title': 'Fehler beim Senden',
    'subscribe.error.message': 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    
    // Imprint Page
    'imprint.page.title': 'Impressum',
    'imprint.legal.title': 'Angaben gemäß § 5 TMG',
    'imprint.contact.title': 'Kontakt',
    'imprint.management.title': 'Vertretungsberechtigte',
    'imprint.management.desc': 'Geschäftsführung der Rothus Medien AG',
    'imprint.register.title': 'Handelsregister',
    'imprint.register.desc': 'Handelsregister Solothurn',
    'imprint.responsible.title': 'Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV',
    'imprint.liability.title': 'Haftungsausschluss',
    'imprint.liability.content.title': 'Haftung für Inhalte',
    'imprint.liability.content.text': 'Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht unter der Verpflichtung, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.',
    'imprint.liability.links.title': 'Haftung für Links',
    'imprint.liability.links.text': 'Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.',
    'imprint.copyright.title': 'Urheberrecht',
    'imprint.copyright.text': 'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem schweizerischen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.',
    
    // Privacy Page
    'privacy.page.title': 'Datenschutzerklärung',
    'privacy.date': 'Stand:',
    'privacy.responsible.title': 'Verantwortliche Stelle',
    'privacy.newsletter.title': 'Newsletter-Anmeldung',
    'privacy.newsletter.purpose': 'Zweck der Datenverarbeitung:',
    'privacy.newsletter.purpose.desc': 'Versendung unseres Newsletters mit Informationen rund um das Langlauf-Magazin "glide".',
    'privacy.newsletter.data': 'Erhobene Daten:',
    'privacy.newsletter.data.desc': 'E-Mail-Adresse, optional: Anrede, Vorname, Nachname',
    'privacy.newsletter.legal': 'Rechtsgrundlage:',
    'privacy.newsletter.legal.desc': 'Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) bzw. Art. 13 nDSG (Schweizer Datenschutzgesetz)',
    'privacy.newsletter.storage': 'Speicherdauer:',
    'privacy.newsletter.storage.desc': 'Bis zum Widerruf Ihrer Einwilligung oder Löschung des Newsletters',
    'privacy.newsletter.third': 'Übermittlung an Dritte:',
    'privacy.newsletter.third.desc': 'Ihre Daten werden nicht an Dritte weitergegeben, außer zur technischen Abwicklung des Newsletter-Versands.',
    'privacy.rights.title': 'Ihre Rechte',
    'privacy.rights.intro': 'Sie haben folgende Rechte bezüglich Ihrer personenbezogenen Daten:',
    'privacy.rights.withdrawal': 'Widerrufsrecht: Sie können Ihre Einwilligung jederzeit widerrufen',
    'privacy.rights.access': 'Auskunftsrecht: Recht auf Auskunft über Ihre gespeicherten Daten',
    'privacy.rights.rectification': 'Berichtigungsrecht: Recht auf Korrektur unrichtiger Daten',
    'privacy.rights.deletion': 'Löschungsrecht: Recht auf Löschung Ihrer Daten',
    'privacy.rights.objection': 'Widerspruchsrecht: Recht auf Widerspruch gegen die Datenverarbeitung',
    'privacy.unsubscribe.title': 'Abmeldung vom Newsletter',
    'privacy.unsubscribe.intro': 'Sie können sich jederzeit vom Newsletter abmelden:',
    'privacy.unsubscribe.link': 'Über den Abmelde-Link in jeder Newsletter-E-Mail',
    'privacy.unsubscribe.page': 'Über unsere Abmelde-Seite:',
    'privacy.unsubscribe.page.link': 'Newsletter abmelden',
    'privacy.unsubscribe.email': 'Per E-Mail an: [Ihre E-Mail-Adresse]',
    'privacy.cookies.title': 'Cookies',
    'privacy.cookies.intro': 'Wir verwenden Cookies, um die Funktionalität unserer Website zu gewährleisten. Sie können der Verwendung von Cookies über unser Cookie-Banner zustimmen oder diese ablehnen.',
    'privacy.cookies.necessary': 'Notwendige Cookies: Diese sind für die grundlegende Funktionalität der Website erforderlich.',
    'privacy.contact.title': 'Kontakt bei Datenschutzfragen',
    'privacy.contact.intro': 'Bei Fragen zum Datenschutz oder zur Ausübung Ihrer Rechte kontaktieren Sie uns unter:',
    'privacy.complaint.title': 'Beschwerderecht',
    'privacy.complaint.intro': 'Sie haben das Recht, sich bei der zuständigen Aufsichtsbehörde zu beschweren, wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer personenbezogenen Daten gegen geltendes Datenschutzrecht verstößt.',
    'privacy.complaint.swiss': 'Schweiz: Eidgenössischer Datenschutz- und Öffentlichkeitsbeauftragte (EDÖB)',
    
    // Terms Page
    'terms.page.title': 'Allgemeine Geschäftsbedingungen (AGB)',
    'terms.page.subtitle': 'für Abonnenten/Nutzer',
    'terms.page.version': 'Version Januar 2025, gültig bis Widerruf',
    
    // Countdown Landing
    'countdown.title': 'Das erste Langlauf-Magazin',
    'countdown.subtitle': 'der Schweiz erscheint in:',
    'countdown.days': 'TAGE',
    'countdown.hours': 'STUNDEN',
    'countdown.minutes': 'MINUTEN',
    'countdown.seconds': 'SEKUNDEN',
    'countdown.date': 'Erscheinung 15. Oktober 2025',
    'countdown.updates': 'Erhalten Sie exklusive Updates zum Start',
    'countdown.salutation.placeholder': 'Anrede',
    'countdown.salutation.mr': 'Herr',
    'countdown.salutation.mrs': 'Frau',
    'countdown.salutation.diverse': 'Divers',
    'countdown.firstName': 'Vorname',
    'countdown.lastName': 'Nachname',
    'countdown.email': 'Ihre E-Mail-Adresse',
    'countdown.submit': 'ANMELDEN',
    'countdown.submitting': 'Anmelden...',
    'countdown.privacy': 'Ich stimme zu, dass meine Daten zum Versand des Newsletters gespeichert werden. Mit der Anmeldung akzeptiere ich die',
    'countdown.privacy.link': 'Datenschutzerklärung',
    'countdown.unsubscribe': 'wieder abmelden',
    'countdown.success': 'Vielen Dank! Sie erhalten bald Updates zum Langlauf-Magazin.',
    
    // Newsletter
    'newsletter.title': 'BLEIBEN SIE INFORMIERT',
    'newsletter.description': 'Erhalten Sie exklusive Einblicke in die Welt des Langlaufs und seien Sie die Ersten, die von unserem neuen Magazin erfahren.',
    'newsletter.email.placeholder': 'Ihre E-Mail-Adresse',
    'newsletter.submit': 'ANMELDEN',
    'newsletter.submitting': 'Anmelden...',
    'newsletter.privacy.full': 'Ich stimme zu, dass meine E-Mail-Adresse zum Versand des Newsletters gespeichert wird. Ich kann mich jederzeit wieder abmelden. Mit der Anmeldung akzeptiere ich die',
    'newsletter.privacy.policy': 'Datenschutzerklärung',
    'newsletter.footer.text': 'Sie können sich jederzeit wieder abmelden. Wir respektieren Ihre Privatsphäre.',
    'newsletter.footer.unsubscribe': 'Newsletter abmelden',
    'newsletter.footer.privacy': 'Datenschutzerklärung',
    
    // Toasts
    'toast.incomplete': 'Eingabe unvollständig',
    'toast.incomplete.desc': 'Bitte geben Sie Ihre E-Mail ein und akzeptieren Sie die Datenschutzerklärung.',
    'toast.already.registered': 'Bereits registriert',
    'toast.already.registered.desc': 'Diese E-Mail-Adresse ist bereits für den Newsletter angemeldet.',
    'toast.success': 'Erfolgreich angemeldet!',
    'toast.success.desc': 'Sie erhalten bald Updates zu unserem Langlauf-Magazin.',
    'toast.error': 'Fehler bei der Anmeldung',
    'toast.error.desc': 'Bitte versuchen Sie es später erneut.',
    
    // Admin
    'admin.login.title': 'Admin Login',
    'admin.login.subtitle': 'Bitte geben Sie das Passwort ein',
    'admin.login.password': 'Passwort',
    'admin.login.submit': 'Anmelden',
    'admin.login.success': 'Erfolgreich angemeldet',
    'admin.login.error': 'Falsches Passwort',
    'admin.logout': 'Abmelden',
    'admin.title': 'Newsletter Administration',
    'admin.subtitle': 'Verwalten Sie Ihre Newsletter-Abonnenten',

    // Featured Stories
    'featured.stories.title': 'NEUESTE STORIES',
    'featured.stories.view.all': 'Alle Stories ansehen',
    'featured.stories.no.stories': 'Keine Stories verfügbar.',
    'featured.stories.loading': 'Lade Stories...',

    // Expectations Section
    'expectations.title': 'Was Sie erwartet',
    'expectations.expertise.title': 'Expertenwissen',
    'expectations.expertise.description': 'Professionelle Tipps von Olympiasiegern, Trainern und Langlauflehrern und Lehrerinnen',
    'expectations.community.title': 'Community',
    'expectations.community.description': 'Verbinden Sie sich mit der wachsenden Langlauf-Community der Schweiz',
    'expectations.content.title': 'Aktuelle Inhalte',
    'expectations.content.description': 'Regelmässig neue Inhalte zu Technik, Ausrüstung und den besten Loipen',

    // Footer
    'footer.navigation': 'Navigation',
    'footer.legal.contact': 'Rechtliches / Kontakt',
    'footer.copyright': 'Alle Rechte vorbehalten.',
    'footer.tagline': 'Das Schweizer Langlauf-Magazin',
    'footer.startseite': 'Startseite',
    'footer.stories': 'Stories',
    'footer.abonnieren': 'Abonnieren',
    'footer.newsletter': 'Newsletter',
    'footer.mediadaten': 'Mediadaten',
    'footer.kontakt': 'Kontakt',
    'footer.datenschutz': 'Datenschutzerklärung',
    'footer.agb': 'AGB',
    'footer.impressum': 'Impressum',
    'footer.unsubscribe': 'Newsletter abmelden',
    'footer.cookie_settings': 'Cookie-Einstellungen',

    // Story pages
    'stories.title': 'Stories • glide Magazin',
    'stories.description': 'Stories und Blogbeiträge des glide Langlauf-Magazins.',
    'stories.loading': 'Laden…',
    'stories.all': 'Alle',
    'stories.featured': 'Featured',
    'stories.pagination.back': 'Zurück',
    'stories.pagination.next': 'Weiter',
    'story.loading': 'Laden…',
    'story.not.found.title': 'Beitrag nicht gefunden',
    'story.not.found.back': 'Zur Übersicht',
    'story.back.to.stories': 'Zurück zu allen Stories',
    'story.more.stories': 'Weitere Stories',
    'story.incomplete.notice': 'Hinweis:',
    'story.incomplete.text': 'Dieser Artikel wird möglicherweise noch vervollständigt.',

    // 404 Page
    'notfound.title': '404',
    'notfound.subtitle': 'Seite nicht gefunden',
    'notfound.description': 'Die gesuchte Seite konnte nicht gefunden werden.',
    'notfound.back.home': 'Zur Startseite',

    // Unsubscribe page
    'unsubscribe.title': 'Newsletter abmelden',
    'unsubscribe.description': 'Geben Sie Ihre E-Mail-Adresse ein, um sich vom Newsletter abzumelden.',
    'unsubscribe.success': 'Sie wurden erfolgreich vom Newsletter abgemeldet.',
    'unsubscribe.email.placeholder': 'Ihre E-Mail-Adresse',
    'unsubscribe.submit': 'Vom Newsletter abmelden',
    'unsubscribe.submitting': 'Abmelden...',
    'unsubscribe.sorry.text': 'Es tut uns leid, Sie gehen zu sehen. Sie können sich jederzeit wieder anmelden.',
    'unsubscribe.success.title': 'Abmeldung erfolgreich',
    'unsubscribe.success.message': 'Sie erhalten keine weiteren Newsletter-E-Mails von uns.',
    'unsubscribe.home': 'Zur Startseite',
    'unsubscribe.contact': 'Haben Sie Fragen? Kontaktieren Sie uns unter',
    'unsubscribe.confirmation.sent': 'Bestätigungs-E-Mail gesendet',
    'unsubscribe.confirmation.description': 'Bitte überprüfen Sie Ihr E-Mail-Postfach und klicken Sie auf den Bestätigungslink.',

    // Terms page sections
    'terms.scope.title': 'I. Anwendungsbereich',
    'terms.scope.text': 'Die vorliegenden AGB betreffen alle Beziehungen zwischen der Rothus Medien AG (nachfolgend kollektiv „Anbieter") und den Nutzerinnen und Nutzern (nachfolgend „Nutzer") von Dienstleistungen und Produkten des Anbieters. Mit der Nutzung der Produkte akzeptiert der Nutzer die vorliegenden AGB und verpflichtet sich, diese einzuhalten.',
    'terms.usage.title': 'II. Nutzungsbedingungen',
    'terms.usage.text': 'Die Produkte dienen ausschliesslich der persönlichen, nicht kommerziellen Nutzung durch den Nutzer zu privaten Zwecken.',
    'terms.effective': 'Inkrafttreten:',
    'terms.effective.text': 'Diese AGB wurden per 1.1.2024 in Kraft gesetzt.',

    // Hero
    'hero.title': 'glide Nº 3 – das Schweizer Langlauf-Magazin: Erlebnis Engadiner, Nadja Kälin, Sportsonnenbrillen und Seeloipen.',
    'hero.order': 'Bestellen',

    // Index facts strip
    'index.fact.first': 'Erste Langlaufzeitschrift der Schweiz',
    'index.fact.issues': '4x Ausgaben pro Jahr',
    'index.fact.content': 'Reportagen, Tests und Inspiration',
    'index.pull': 'Ziehen zum Aktualisieren',
    'index.release': 'Loslassen zum Aktualisieren',

    // Shop
    'shop.category.all': 'Alle',
    'shop.category.combos': 'Kombi-Angebote',
    'shop.category.subscriptions': 'Abonnements',
    'shop.category.accessories': 'Accessoires',
    'shop.category.single_issues': 'Einzelausgaben',
    'shop.category.combos.short': 'Kombi',
    'shop.category.subscriptions.short': 'Abos',
    'shop.category.accessories.short': 'Zubehör',
    'shop.category.single_issues.short': 'Ausgaben',

    // Common order form
    'order.form.salutation': 'Anrede',
    'order.form.choose': 'Bitte wählen',
    'order.form.mr': 'Herr',
    'order.form.mrs': 'Frau',
    'order.form.family': 'Familie',
    'order.form.firstname': 'Vorname *',
    'order.form.lastname': 'Nachname *',
    'order.form.email': 'E-Mail *',
    'order.form.phone': 'Telefon',
    'order.form.address': 'Strasse *',
    'order.form.zip': 'PLZ *',
    'order.form.city': 'Ort *',
    'order.form.country': 'Land',
    'order.form.message': 'Nachricht (optional)',
    'order.form.message.placeholder': 'Ihre Nachricht an uns...',
    'order.form.gift': 'Als Geschenk bestellen',
    'order.form.gift.desc': 'Das Magazin wird an eine andere Adresse geliefert',
    'order.form.gift.title': 'Geschenk-Lieferadresse',
    'order.form.gift.salutation': 'Anrede',
    'order.form.gift.firstname': 'Vorname',
    'order.form.gift.lastname': 'Nachname',
    'order.form.gift.address': 'Strasse',
    'order.form.gift.zip': 'PLZ',
    'order.form.gift.city': 'Ort',
    'order.form.privacy': 'Ich akzeptiere die',
    'order.form.privacy.link': 'AGB',
    'order.form.submit': 'Abonnement bestellen',
    'order.form.submitting': 'Bestellung wird verarbeitet...',
    'order.form.success': 'Bestellung erfolgreich!',
    'order.form.success.desc': 'Vielen Dank für Ihre Bestellung. Sie erhalten bald eine Bestätigung per E-Mail.',
    'order.form.error': 'Fehler bei der Bestellung',
    'order.form.error.desc': 'Bitte versuchen Sie es später erneut.',
    'order.form.incomplete': 'Eingabe unvollständig',
    'order.form.incomplete.desc': 'Bitte füllen Sie alle Pflichtfelder aus und akzeptieren Sie die AGB.',
    'order.form.order_now': 'Jetzt bestellen',
    'order.form.sending': 'Wird gesendet...',

    // Ostern
    'ostern.title': 'Oster-Spezialangebot',
    'ostern.subtitle': 'Frohe Ostern! Sichern Sie sich jetzt unser exklusives Osterangebot 🐰',
    'ostern.offer.label': '🐰 Oster-Spezialangebot',
    'ostern.offer.name': '🐣 Oster-Abonnement – 4 Ausgaben',
    'ostern.offer.price': 'Zum Spezialpreis von CHF 29.–',
    'ostern.gift': '🎁 Als Oster-Geschenk bestellen',
    'ostern.gift.title': '🐰 Geschenk-Lieferadresse',
    'ostern.message.placeholder': 'Oster-Grüsse oder Ihre Nachricht an uns... 🐣',
    'ostern.submit': '🐰 Jetzt Oster-Abo bestellen',
    'ostern.footer': '🐣 Frohe Ostern wünscht das glide-Team! 🐣',
    'ostern.success': '🐰 Bestellung erfolgreich!',
    'ostern.success.desc': 'Frohe Ostern! Sie erhalten bald eine Bestätigung per E-Mail.',

    // ProNordic
    'pronordic.subtitle': 'ProNordic Spezial-Abonnement',
    'pronordic.offer.label': 'ProNordic Spezial-Abonnement',
    'pronordic.offer.name': 'Spezial-Abonnement für ProNordic-Mitglieder',
    'pronordic.offer.price': '4 Ausgaben für CHF 29.-',

    // Leserangebot
    'leserangebot.title': 'Leserangebot',
    'leserangebot.subtitle': 'Exklusives glide-Set für unsere Leser',
    'leserangebot.product.title': 'Jetzt im glide-Set:',
    'leserangebot.buff.title': 'Buff Multifunktionstuch',
    'leserangebot.buff.desc': 'Eines für alles – das ist das Motto, welches das Buff Multifunktionstuch am besten beschreibt! Ob als Schal, Mütze, Stirnband oder Kopftuch – das Buff kann alles.',
    'leserangebot.glove.title': 'Langlaufhandschuhe von Chiba',
    'leserangebot.glove.desc': 'Warm, winddicht und geschmeidig – diese Handschuhe verbinden Softshell-Komfort mit feinem Nappa-Grip. Primaloft-Wärme und ergonomische Passform schützen deine Hände bei jedem Abenteuer.',
    'leserangebot.offer.select': 'Angebot auswählen *',
    'leserangebot.offer.set': 'glide-Set (Buff + Handschuhe)',
    'leserangebot.offer.set_abo': 'glide-Set inkl. glide-Jahresabo (4 Ausgaben)',
    'leserangebot.glove_size': 'Grösse Handschuhe *',
    'leserangebot.newsletter': 'Ich möchte den Newsletter abonnieren',
    'leserangebot.email_note': '(erforderlich für Rechnung)',
    'leserangebot.privacy.title': 'Datenschutzerklärung akzeptieren *',
    'leserangebot.privacy.desc': 'Ich habe die',
    'leserangebot.privacy.link': 'Datenschutzerklärung',
    'leserangebot.privacy.read': 'gelesen und akzeptiere diese.',
    'leserangebot.shipping': 'Nur solange Vorrat. Preise inkl. MwSt.',
    'leserangebot.shipping2': 'Für Bestellungen unter CHF 60.– zzgl. CHF 9.– Kleinmengenzuschlag.',

    // Umfrage
    'umfrage.title': 'Freelancer Feedback',
    'umfrage.subtitle': 'Langlaufmagazin Schweiz – Planung Winter 2026/27',
    'umfrage.intro': 'Vielen Dank nochmals für eure Mitarbeit an den ersten Ausgaben unseres Langlaufmagazins. Damit wir die nächste Saison frühzeitig planen können, würden wir uns über euer kurzes Feedback freuen.',
    'umfrage.step': 'Schritt',
    'umfrage.of': 'von',
    'umfrage.steps.review': 'Rückblick',
    'umfrage.steps.collab': 'Zusammenarbeit',
    'umfrage.steps.topics': 'Themenideen',
    'umfrage.steps.planning': 'Planung',
    'umfrage.steps.gifts': 'Geschenk-Abos',
    'umfrage.gift.intro': 'Als Dankeschön für deine Unterstützung darfst du uns 3 Adressen angeben. Diese Personen erhalten das GLIDE Langlaufmagazin kostenlos für ein Jahr.',
    'umfrage.sender.title': 'Deine Angaben (Freelancer / Absender)',
    'umfrage.gift.addresses': '3 Geschenk-Adressen (Gratis-Jahresabo)',
    'umfrage.gift.address': 'Geschenkadresse',
    'umfrage.gift.optional': 'Optional – nur ausfüllen wenn eine Geschenkadresse angegeben werden soll',
    'umfrage.back': 'Zurück',
    'umfrage.next': 'Weiter',
    'umfrage.submit': 'Absenden',
    'umfrage.submitting': 'Wird gesendet…',
    'umfrage.success.title': 'Vielen Dank!',
    'umfrage.success.desc': 'Vielen Dank für dein Feedback und deine Unterstützung beim Langlaufmagazin!',
    'umfrage.success.sub': 'Wir freuen uns sehr über deine Rückmeldung und kümmern uns gerne um die Gratis-Jahresabos.',
    'umfrage.fill_required': 'Bitte alle Pflichtfelder ausfüllen',
    'umfrage.error': 'Fehler beim Absenden',
    'umfrage.answer.placeholder': 'Deine Antwort…',
    'umfrage.other.placeholder': 'Bitte angeben…',
    'umfrage.required_if': 'Pflichtfeld (wenn Adresse ausgefüllt)',
    'umfrage.required': 'Pflichtfeld',
    'umfrage.invalid_email': 'Ungültige E-Mail',
    // Survey questions
    'umfrage.q.satisfaction': 'Wie zufrieden bist du insgesamt mit den ersten drei Ausgaben des Magazins?',
    'umfrage.q.worked_well': 'Was hat deiner Meinung nach besonders gut funktioniert?',
    'umfrage.q.improve': 'Was sollten wir bei den nächsten Ausgaben unbedingt verbessern?',
    'umfrage.q.design': 'Wie beurteilst du das Magazindesign und Layout?',
    'umfrage.q.design_feedback': 'Gibt es Aspekte beim Design, die wir optimieren sollten? (z. B. Bildsprache, Lesbarkeit, Struktur, Länge der Artikel)',
    'umfrage.q.collab': 'Wie hast du die Zusammenarbeit mit der Redaktion erlebt?',
    'umfrage.q.briefing': 'Waren die Briefings und Themenvorgaben klar genug?',
    'umfrage.q.planning_help': 'Was würde dir helfen, deine Arbeit für kommende Ausgaben noch besser zu planen?',
    'umfrage.q.topics': 'An welchen Themen würdest du gerne arbeiten? (z. B. Athletenporträts, Materialtests, Trainingsreportagen, Destinationen, Nachwuchs, Szene)',
    'umfrage.q.story_ideas': 'Hast du konkrete Story-Ideen für kommende Ausgaben?',
    'umfrage.q.missing': 'Welche Themen fehlen deiner Meinung nach generell im Schweizer Langlaufjournalismus?',
    'umfrage.q.sections': 'Für welche Rubriken würdest du gerne schreiben oder arbeiten?',
    'umfrage.q.other_section': 'Andere Rubrik',
    'umfrage.q.contribute': 'Möchtest du auch bei den nächsten Ausgaben wieder als Freelancer mitarbeiten?',
    'umfrage.q.role': 'Welche Rolle könntest du dir vorstellen?',
    'umfrage.q.other_role': 'Andere Rolle',
    'umfrage.q.count': 'Wie viele Beiträge könntest du dir pro Saison ungefähr vorstellen?',
    'umfrage.q.availability': 'Gibt es Zeiträume im Winter, in denen du besonders verfügbar oder weniger verfügbar bist?',
    'umfrage.q.final': 'Weitere Ideen, Feedback oder Wünsche für die nächste Ausgabe?',
    'umfrage.q.promote_own': 'Würdest du das Magazin aktiv über deine Kanäle (Social Media, Netzwerk) unterstützen?',
    'umfrage.q.promote_access': 'Würdest du das Magazin aktiv direkt über unsere Kanäle unterstützen, wenn du einen Zugang dazu hättest?',
    // Survey options
    'umfrage.opt.very_satisfied': 'Sehr zufrieden',
    'umfrage.opt.satisfied': 'Zufrieden',
    'umfrage.opt.neutral': 'Neutral',
    'umfrage.opt.less_satisfied': 'Weniger zufrieden',
    'umfrage.opt.unsatisfied': 'Unzufrieden',
    'umfrage.opt.very_good': 'Sehr gelungen',
    'umfrage.opt.good': 'Gut',
    'umfrage.opt.needs_improvement': 'Verbesserungswürdig',
    'umfrage.opt.collab_very_good': 'Sehr gut',
    'umfrage.opt.collab_good': 'Gut',
    'umfrage.opt.yes_clear': 'Ja, sehr klar',
    'umfrage.opt.mostly_clear': 'Meistens klar',
    'umfrage.opt.partly_unclear': 'Teilweise unklar',
    'umfrage.opt.yes_gladly': 'Ja, sehr gerne',
    'umfrage.opt.probably': 'Wahrscheinlich',
    'umfrage.opt.unsure': 'Unsicher',
    'umfrage.opt.rather_not': 'Eher nicht',
    'umfrage.opt.author': 'Autor/in',
    'umfrage.opt.photographer': 'Fotograf/in',
    'umfrage.opt.both': 'Beides',
    'umfrage.opt.other_role': 'Andere Rolle',
    'umfrage.opt.1_contribution': '1 Beitrag',
    'umfrage.opt.2_3_contributions': '2–3 Beiträge',
    'umfrage.opt.4_plus': '4+ Beiträge',
    'umfrage.opt.yes': 'Ja',
    'umfrage.opt.partially': 'Teilweise',
    // Section options
    'umfrage.section.reportage': 'Reportage',
    'umfrage.section.interview': 'Interview / Porträt',
    'umfrage.section.material': 'Material / Technik',
    'umfrage.section.training': 'Training / Coaching',
    'umfrage.section.scene': 'Szene / Community',
    'umfrage.section.photo': 'Fotoreportage',
    'umfrage.section.other': 'Andere',
    // Form labels
    'umfrage.form.firstname': 'Vorname',
    'umfrage.form.lastname': 'Nachname',
    'umfrage.form.email': 'E-Mail',
    'umfrage.form.phone': 'Telefon',
    'umfrage.form.street': 'Strasse',
    'umfrage.form.zip': 'PLZ',
    'umfrage.form.city': 'Ort',
    'umfrage.form.country': 'Land',
    'umfrage.form.company': 'Firma',
  },
  en: {
    // Navigation
    'nav.stories': 'Stories',
    'nav.regions': 'REGIONS',
    'nav.guides': 'GUIDES',
    'nav.about': 'ABOUT US',
    'nav.home': 'Home',
    'nav.newsletter': 'Newsletter',
    'nav.subscribe': 'Subscribe',
    'nav.media': 'Media',
    'nav.contact': 'Contact',
    'nav.magazine': 'Magazine',
    
    // Common
    'home': 'Home',
    'contact': 'Contact',
    'newsletter': 'Newsletter',
    'media.data': 'Media Data',
    'subscribe': 'Subscribe',
    'privacy': 'Privacy',
    'imprint': 'Imprint',
    'terms': 'Terms',
    'back': 'Back',
    'phone': 'Phone',
    'email': 'Email',
    'message': 'Message',
    'send': 'Send',
    'sending': 'Sending...',
    'required': 'Required',
    
    // Newsletter Page
    'newsletter.page.title': 'Newsletter Subscription',
    'newsletter.page.description': 'Get the latest updates on Switzerland\'s most beautiful cross-country skiing areas, track conditions and exclusive insider tips delivered directly to your inbox.',
    'newsletter.signup.title': 'Sign up for our newsletter now',
    'newsletter.form.salutation': 'Salutation',
    'newsletter.form.salutation.mr': 'Mr',
    'newsletter.form.salutation.ms': 'Ms',
    'newsletter.form.salutation.diverse': 'Mx',
    'newsletter.form.firstname': 'First Name',
    'newsletter.form.lastname': 'Last Name',
    'newsletter.form.email': 'Email *',
    'newsletter.form.privacy': 'I accept the',
    'newsletter.form.privacy.link': 'privacy policy',
    'newsletter.form.submit': 'Subscribe to Newsletter',
    'newsletter.form.submitting': 'Subscribing...',
    'newsletter.success.message': 'Thank you! You will soon receive updates about the cross-country skiing magazine.',
    
    // Contact Page
    'contact.page.title': 'Contact',
    'contact.page.description': 'Do you have questions about the Swiss cross-country skiing magazine or would you like to get in touch with us? We look forward to your message.',
    'contact.form.name': 'Name *',
    'contact.form.company': 'Company/Organization',
    'contact.form.subject': 'Subject',
    'contact.form.subject.general': 'General Inquiry',
    'contact.form.subject.editorial': 'Editorial',
    'contact.form.subject.advertising': 'Advertising/Marketing',
    'contact.form.subject.subscription': 'Subscription',
    'contact.form.subject.support': 'Technical Support',
    'contact.form.message': 'Your Message *',
    'contact.form.submit': 'Send Message',
    'contact.form.submitting': 'Sending...',
    'contact.success.title': 'Message Sent!',
    'contact.success.message': 'Thank you for your message. We will get back to you soon.',
    'contact.error.title': 'Sending Error',
    'contact.error.message': 'Please try again later.',
    'contact.error.incomplete': 'Please fill in all required fields.',
    
    // Media Data Page
    'media.page.title': 'Media Data',
    'media.page.description': 'Request the current media data. After entering your contact details, the PDF will be unlocked.',
    'media.page.subtitle': 'Target Audience and Reach',
    'media.arrow.text': 'Discover our Target Audience & Reach',
    'media.target.section.title': 'Our Target Audience & Reach',
    'media.target.section.description': 'Reach an engaged community of cross-country skiing enthusiasts and outdoor lovers',
    'media.target.audience': 'Target Audience',
    'media.circulation.distribution': 'Distribution & Circulation',
    'media.age.years': 'Years',
    'media.sporty.active': 'Sporty',
    'media.active': 'Active',
    'media.nature.connected': 'Nature-',
    'media.connected': 'connected',
    'media.income.level': 'Middle to',
    'media.income.high': 'high income',
    'media.circulation.per.issue': 'Circulation per issue',
    'media.printed.copies': 'Printed copies',
    'media.subscribers': 'Subscribers',
    'media.loyal.readership': 'Loyal readership',
    'media.newsletter.recipients': 'Newsletter',
    'media.recipients': 'Recipients',
    'media.publishing': 'Publishing',
    'media.quarterly': '4× yearly',
    'media.october': 'October',
    'media.december': 'December',
    'media.february': 'February',
    'media.july': 'July',
    'media.distribution': 'Distribution',
    'media.nationwide': 'Switzerland-wide',
    'media.focus.hotspots': 'Focus on cross-country skiing hotspots',
    'media.giveaway': 'Give-away',
    'media.centers.shops': 'Cross-country centers & sports shops',
    'media.facts.title': 'Cross-country skiing facts in Switzerland',
    'media.facts.trails': 'Prepared trails',
    'media.facts.organizations': '160 organizations',
    'media.facts.marathon': 'Engadin Ski Marathon',
    'media.facts.participants': 'Participants annually',
    'media.facts.swiss.sport': 'Of Swiss people exercise',
    'media.facts.regular.sport': 'regularly',
    'media.form.company': 'Company',
    'media.form.firstname': 'First Name *',
    'media.form.lastname': 'Last Name *',
    'media.form.purpose': 'Purpose / Comment',
    'media.form.privacy.accept': 'I have read the',
    'media.form.privacy.read': 'and agree.',
    'media.form.submit': 'Unlock PDF',
    'media.form.submitting': 'Sending...',
    'media.pdf.title': 'Media Data PDF',
    'media.pdf.download': 'Download',
    'media.pdf.blocked': 'Embedding was blocked by the browser. Please use the download button above.',
    
    // Subscribe Page
    'subscribe.page.title': 'Choose your glide subscription',
    'subscribe.form.firstname': 'First Name *',
    'subscribe.form.lastname': 'Last Name *',
    'subscribe.form.address': 'Street *',
    'subscribe.form.zip': 'Zip Code *',
    'subscribe.form.city': 'City *',
    'subscribe.form.gift.option': 'Order as a gift',
    'subscribe.form.gift.description': 'The magazine will be delivered to a different address',
    'subscribe.form.gift.address': 'Gift Delivery Address',
    'subscribe.form.gift.salutation': 'Salutation',
    'subscribe.form.gift.choose': 'Please choose',
    'subscribe.form.gift.mr': 'Mr',
    'subscribe.form.gift.ms': 'Ms',
    'subscribe.form.subscription': 'Choose your subscription',
    'subscribe.form.yearly': 'Annual Subscription',
    'subscribe.form.yearly.desc': '4 issues for CHF 49.-',
    'subscribe.form.twoyear': '2-Year Subscription',
    'subscribe.form.twoyear.desc': '8 issues for CHF 78.-',
    'subscribe.form.message.optional': 'Message (optional)',
    'subscribe.form.message.placeholder': 'Your message...',
    'subscribe.form.terms.accept': 'I accept the',
    'subscribe.form.terms.link': 'Terms and Conditions',
    'subscribe.form.submit': 'Order Subscription',
    'subscribe.form.submitting': 'Ordering...',
    'subscribe.success.title': 'Order Successful!',
    'subscribe.success.message': 'Your magazine order has been successfully submitted. You will receive a confirmation by email.',
    'subscribe.error.terms': 'Please accept the Terms and Conditions.',
    'subscribe.error.title': 'Sending Error',
    'subscribe.error.message': 'An error occurred. Please try again.',
    
    // Imprint Page
    'imprint.page.title': 'Imprint',
    'imprint.legal.title': 'Information according to § 5 TMG',
    'imprint.contact.title': 'Contact',
    'imprint.management.title': 'Authorized Representatives',
    'imprint.management.desc': 'Management of Rothus Medien AG',
    'imprint.register.title': 'Commercial Register',
    'imprint.register.desc': 'Commercial Register Solothurn',
    'imprint.responsible.title': 'Responsible for content according to § 55 para. 2 RStV',
    'imprint.liability.title': 'Disclaimer',
    'imprint.liability.content.title': 'Liability for Content',
    'imprint.liability.content.text': 'As a service provider, we are responsible for our own content on these pages according to general laws pursuant to § 7 para. 1 TMG. According to §§ 8 to 10 TMG, however, we are not under the obligation to monitor transmitted or stored third-party information or to research circumstances that indicate illegal activity.',
    'imprint.liability.links.title': 'Liability for Links',
    'imprint.liability.links.text': 'Our offer contains links to external websites of third parties, on whose contents we have no influence. Therefore, we cannot assume any liability for these external contents. The respective provider or operator of the pages is always responsible for the contents of the linked pages.',
    'imprint.copyright.title': 'Copyright',
    'imprint.copyright.text': 'The content and works created by the site operators on these pages are subject to Swiss copyright law. Duplication, processing, distribution and any kind of exploitation outside the limits of copyright law require the written consent of the respective author or creator.',
    
    // Privacy Page
    'privacy.page.title': 'Privacy Policy',
    'privacy.date': 'As of:',
    'privacy.responsible.title': 'Responsible Party',
    'privacy.newsletter.title': 'Newsletter Registration',
    'privacy.newsletter.purpose': 'Purpose of data processing:',
    'privacy.newsletter.purpose.desc': 'Sending our newsletter with information about the cross-country skiing magazine "glide".',
    'privacy.newsletter.data': 'Collected data:',
    'privacy.newsletter.data.desc': 'Email address, optional: salutation, first name, last name',
    'privacy.newsletter.legal': 'Legal basis:',
    'privacy.newsletter.legal.desc': 'Art. 6 para. 1 lit. a GDPR (consent) or Art. 13 nDSG (Swiss Data Protection Act)',
    'privacy.newsletter.storage': 'Storage duration:',
    'privacy.newsletter.storage.desc': 'Until revocation of your consent or deletion of the newsletter',
    'privacy.newsletter.third': 'Transfer to third parties:',
    'privacy.newsletter.third.desc': 'Your data will not be passed on to third parties, except for the technical handling of newsletter dispatch.',
    'privacy.rights.title': 'Your Rights',
    'privacy.rights.intro': 'You have the following rights regarding your personal data:',
    'privacy.rights.withdrawal': 'Right of withdrawal: You can withdraw your consent at any time',
    'privacy.rights.access': 'Right of access: Right to information about your stored data',
    'privacy.rights.rectification': 'Right of rectification: Right to correction of incorrect data',
    'privacy.rights.deletion': 'Right of deletion: Right to deletion of your data',
    'privacy.rights.objection': 'Right of objection: Right to object to data processing',
    'privacy.unsubscribe.title': 'Newsletter Unsubscription',
    'privacy.unsubscribe.intro': 'You can unsubscribe from the newsletter at any time:',
    'privacy.unsubscribe.link': 'Via the unsubscribe link in every newsletter email',
    'privacy.unsubscribe.page': 'Via our unsubscribe page:',
    'privacy.unsubscribe.page.link': 'Unsubscribe from newsletter',
    'privacy.unsubscribe.email': 'By email to: [Your email address]',
    'privacy.cookies.title': 'Cookies',
    'privacy.cookies.intro': 'We use cookies to ensure the functionality of our website. You can consent to or reject the use of cookies via our cookie banner.',
    'privacy.cookies.necessary': 'Necessary cookies: These are required for the basic functionality of the website.',
    'privacy.contact.title': 'Contact for Privacy Questions',
    'privacy.contact.intro': 'For questions about data protection or to exercise your rights, contact us at:',
    'privacy.complaint.title': 'Right to Complain',
    'privacy.complaint.intro': 'You have the right to file a complaint with the competent supervisory authority if you believe that the processing of your personal data violates applicable data protection law.',
    'privacy.complaint.swiss': 'Switzerland: Federal Data Protection and Information Commissioner (FDPIC)',
    
    // Terms Page
    'terms.page.title': 'General Terms and Conditions',
    'terms.page.subtitle': 'for Subscribers/Users',
    'terms.page.version': 'Version January 2025, valid until revoked',
    
    // Countdown Landing
    'countdown.title': 'Switzerland\'s first cross-country skiing magazine',
    'countdown.subtitle': 'will be published in:',
    'countdown.days': 'DAYS',
    'countdown.hours': 'HOURS',
    'countdown.minutes': 'MINUTES',
    'countdown.seconds': 'SECONDS',
    'countdown.date': 'Release October 15, 2025',
    'countdown.updates': 'Receive exclusive updates about the launch',
    'countdown.salutation.placeholder': 'Salutation',
    'countdown.salutation.mr': 'Mr',
    'countdown.salutation.mrs': 'Ms',
    'countdown.salutation.diverse': 'Mx',
    'countdown.firstName': 'First Name',
    'countdown.lastName': 'Last Name',
    'countdown.email': 'Your email address',
    'countdown.submit': 'SUBSCRIBE',
    'countdown.submitting': 'Subscribing...',
    'countdown.privacy': 'I agree that my data will be stored for sending the newsletter. By subscribing, I accept the',
    'countdown.privacy.link': 'privacy policy',
    'countdown.unsubscribe': 'unsubscribe',
    'countdown.success': 'Thank you! You will soon receive updates about the cross-country skiing magazine.',
    
    // Newsletter
    'newsletter.title': 'STAY INFORMED',
    'newsletter.description': 'Get exclusive insights into the world of cross-country skiing and be the first to hear about our new magazine.',
    'newsletter.email.placeholder': 'Your email address',
    'newsletter.submit': 'SUBSCRIBE',
    'newsletter.submitting': 'Subscribing...',
    'newsletter.privacy.full': 'I agree that my email address will be stored for sending the newsletter. I can unsubscribe at any time. By subscribing, I accept the',
    'newsletter.privacy.policy': 'privacy policy',
    'newsletter.footer.text': 'You can unsubscribe at any time. We respect your privacy.',
    'newsletter.footer.unsubscribe': 'Unsubscribe from newsletter',
    'newsletter.footer.privacy': 'Privacy Policy',
    
    // Toasts
    'toast.incomplete': 'Input incomplete',
    'toast.incomplete.desc': 'Please enter your email and accept the privacy policy.',
    'toast.already.registered': 'Already registered',
    'toast.already.registered.desc': 'This email address is already subscribed to the newsletter.',
    'toast.success': 'Successfully subscribed!',
    'toast.success.desc': 'You will soon receive updates about our cross-country skiing magazine.',
    'toast.error': 'Subscription error',
    'toast.error.desc': 'Please try again later.',
    
    // Admin
    'admin.login.title': 'Admin Login',
    'admin.login.subtitle': 'Please enter the password',
    'admin.login.password': 'Password',
    'admin.login.submit': 'Login',
    'admin.login.success': 'Successfully logged in',
    'admin.login.error': 'Wrong password',
    'admin.logout': 'Logout',
    'admin.title': 'Newsletter Administration',
    'admin.subtitle': 'Manage your newsletter subscribers',

    // Featured Stories
    'featured.stories.title': 'LATEST STORIES',
    'featured.stories.view.all': 'View All Stories',
    'featured.stories.no.stories': 'No stories available.',
    'featured.stories.loading': 'Loading stories...',

    // Expectations Section
    'expectations.title': 'What to Expect',
    'expectations.expertise.title': 'Expert Knowledge',
    'expectations.expertise.description': 'Professional tips from Olympic champions, trainers, and cross-country skiing instructors',
    'expectations.community.title': 'Community',
    'expectations.community.description': 'Connect with the growing cross-country skiing community in Switzerland',
    'expectations.content.title': 'Current Content',
    'expectations.content.description': 'Regular new content on technique, equipment, and the best trails',

    // Footer
    'footer.navigation': 'Navigation',
    'footer.legal.contact': 'Legal / Contact',
    'footer.copyright': 'All rights reserved.',
    'footer.tagline': 'The Swiss Cross-Country Skiing Magazine',
    'footer.startseite': 'Home',
    'footer.stories': 'Stories',
    'footer.abonnieren': 'Subscribe',
    'footer.newsletter': 'Newsletter',
    'footer.mediadaten': 'Media Kit',
    'footer.kontakt': 'Contact',
    'footer.datenschutz': 'Privacy Policy',
    'footer.agb': 'Terms',
    'footer.impressum': 'Imprint',
    'footer.unsubscribe': 'Unsubscribe Newsletter',
    'footer.cookie_settings': 'Cookie Settings',

    // Story pages
    'stories.title': 'Stories • glide Magazine',
    'stories.description': 'Stories and blog posts from the glide cross-country skiing magazine.',
    'stories.loading': 'Loading…',
    'stories.all': 'All',
    'stories.featured': 'Featured',
    'stories.pagination.back': 'Back',
    'stories.pagination.next': 'Next',
    'story.loading': 'Loading…',
    'story.not.found.title': 'Article not found',
    'story.not.found.back': 'To overview',
    'story.back.to.stories': 'Back to all Stories',
    'story.more.stories': 'More Stories',
    'story.incomplete.notice': 'Notice:',
    'story.incomplete.text': 'This article may still be completed.',

    // 404 Page
    'notfound.title': '404',
    'notfound.subtitle': 'Page not found',
    'notfound.description': 'The page you are looking for could not be found.',
    'notfound.back.home': 'Return to Home',

    // Unsubscribe page
    'unsubscribe.title': 'Unsubscribe from Newsletter',
    'unsubscribe.description': 'Enter your email address to unsubscribe from the newsletter.',
    'unsubscribe.success': 'You have been successfully unsubscribed from the newsletter.',
    'unsubscribe.email.placeholder': 'Your email address',
    'unsubscribe.submit': 'Unsubscribe from Newsletter',
    'unsubscribe.submitting': 'Unsubscribing...',
    'unsubscribe.sorry.text': 'We\'re sorry to see you go. You can subscribe again at any time.',
    'unsubscribe.success.title': 'Unsubscription successful',
    'unsubscribe.success.message': 'You will not receive any more newsletter emails from us.',
    'unsubscribe.home': 'To Homepage',
    'unsubscribe.contact': 'Have questions? Contact us at',
    'unsubscribe.confirmation.sent': 'Confirmation email sent',
    'unsubscribe.confirmation.description': 'Please check your email inbox and click on the confirmation link.',

    // Terms page sections
    'terms.scope.title': 'I. Scope of Application',
    'terms.scope.text': 'These Terms and Conditions concern all relationships between Rothus Medien AG (hereinafter collectively "Provider") and users (hereinafter "Users") of services and products of the Provider. By using the products, the User accepts these Terms and Conditions and undertakes to comply with them.',
    'terms.usage.title': 'II. Terms of Use',
    'terms.usage.text': 'The products serve exclusively for personal, non-commercial use by the User for private purposes.',
    'terms.effective': 'Effective:',
    'terms.effective.text': 'These Terms and Conditions were put into effect on 1.1.2024.',

    // Hero
    'hero.title': 'glide Nº 3 – the Swiss cross-country skiing magazine: Engadin experience, Nadja Kälin, sports sunglasses and lake trails.',
    'hero.order': 'Order Now',

    // Index facts strip
    'index.fact.first': 'Switzerland\'s first cross-country skiing magazine',
    'index.fact.issues': '4 issues per year',
    'index.fact.content': 'Reports, reviews and inspiration',
    'index.pull': 'Pull to refresh',
    'index.release': 'Release to refresh',

    // Shop
    'shop.category.all': 'All',
    'shop.category.combos': 'Combo Offers',
    'shop.category.subscriptions': 'Subscriptions',
    'shop.category.accessories': 'Accessories',
    'shop.category.single_issues': 'Single Issues',
    'shop.category.combos.short': 'Combos',
    'shop.category.subscriptions.short': 'Subs',
    'shop.category.accessories.short': 'Gear',
    'shop.category.single_issues.short': 'Issues',

    // Common order form
    'order.form.salutation': 'Salutation',
    'order.form.choose': 'Please choose',
    'order.form.mr': 'Mr',
    'order.form.mrs': 'Ms',
    'order.form.family': 'Family',
    'order.form.firstname': 'First Name *',
    'order.form.lastname': 'Last Name *',
    'order.form.email': 'Email *',
    'order.form.phone': 'Phone',
    'order.form.address': 'Street *',
    'order.form.zip': 'Zip Code *',
    'order.form.city': 'City *',
    'order.form.country': 'Country',
    'order.form.message': 'Message (optional)',
    'order.form.message.placeholder': 'Your message...',
    'order.form.gift': 'Order as a gift',
    'order.form.gift.desc': 'The magazine will be delivered to a different address',
    'order.form.gift.title': 'Gift Delivery Address',
    'order.form.gift.salutation': 'Salutation',
    'order.form.gift.firstname': 'First Name',
    'order.form.gift.lastname': 'Last Name',
    'order.form.gift.address': 'Street',
    'order.form.gift.zip': 'Zip Code',
    'order.form.gift.city': 'City',
    'order.form.privacy': 'I accept the',
    'order.form.privacy.link': 'Terms & Conditions',
    'order.form.submit': 'Order Subscription',
    'order.form.submitting': 'Processing order...',
    'order.form.success': 'Order Successful!',
    'order.form.success.desc': 'Thank you for your order. You will receive a confirmation email shortly.',
    'order.form.error': 'Order Error',
    'order.form.error.desc': 'Please try again later.',
    'order.form.incomplete': 'Incomplete',
    'order.form.incomplete.desc': 'Please fill in all required fields and accept the terms.',
    'order.form.order_now': 'Order Now',
    'order.form.sending': 'Sending...',

    // Ostern
    'ostern.title': 'Easter Special Offer',
    'ostern.subtitle': 'Happy Easter! Get our exclusive Easter offer now 🐰',
    'ostern.offer.label': '🐰 Easter Special Offer',
    'ostern.offer.name': '🐣 Easter Subscription – 4 Issues',
    'ostern.offer.price': 'Special price CHF 29.–',
    'ostern.gift': '🎁 Order as an Easter Gift',
    'ostern.gift.title': '🐰 Gift Delivery Address',
    'ostern.message.placeholder': 'Easter greetings or your message... 🐣',
    'ostern.submit': '🐰 Order Easter Subscription',
    'ostern.footer': '🐣 Happy Easter from the glide team! 🐣',
    'ostern.success': '🐰 Order Successful!',
    'ostern.success.desc': 'Happy Easter! You will receive a confirmation email shortly.',

    // ProNordic
    'pronordic.subtitle': 'ProNordic Special Subscription',
    'pronordic.offer.label': 'ProNordic Special Subscription',
    'pronordic.offer.name': 'Special subscription for ProNordic members',
    'pronordic.offer.price': '4 issues for CHF 29.-',

    // Leserangebot
    'leserangebot.title': 'Reader Offer',
    'leserangebot.subtitle': 'Exclusive glide set for our readers',
    'leserangebot.product.title': 'Now in the glide set:',
    'leserangebot.buff.title': 'Buff Multifunctional Headwear',
    'leserangebot.buff.desc': 'One for everything – whether as a scarf, beanie, headband or bandana, the Buff can do it all.',
    'leserangebot.glove.title': 'Cross-Country Gloves by Chiba',
    'leserangebot.glove.desc': 'Warm, windproof and supple – combining softshell comfort with fine nappa grip. Primaloft warmth and ergonomic fit protect your hands on every adventure.',
    'leserangebot.offer.select': 'Select offer *',
    'leserangebot.offer.set': 'glide Set (Buff + Gloves)',
    'leserangebot.offer.set_abo': 'glide Set incl. annual subscription (4 issues)',
    'leserangebot.glove_size': 'Glove Size *',
    'leserangebot.newsletter': 'I would like to subscribe to the newsletter',
    'leserangebot.email_note': '(required for invoice)',
    'leserangebot.privacy.title': 'Accept Privacy Policy *',
    'leserangebot.privacy.desc': 'I have read the',
    'leserangebot.privacy.link': 'Privacy Policy',
    'leserangebot.privacy.read': 'and accept it.',
    'leserangebot.shipping': 'While stocks last. Prices incl. VAT.',
    'leserangebot.shipping2': 'For orders under CHF 60.– plus CHF 9.– small order surcharge.',

    // Umfrage
    'umfrage.title': 'Freelancer Feedback',
    'umfrage.subtitle': 'Cross-Country Skiing Magazine Switzerland – Winter 2026/27 Planning',
    'umfrage.intro': 'Thank you for your contributions to the first issues of our magazine. To plan next season early, we\'d love your brief feedback.',
    'umfrage.step': 'Step',
    'umfrage.of': 'of',
    'umfrage.steps.review': 'Review',
    'umfrage.steps.collab': 'Collaboration',
    'umfrage.steps.topics': 'Topic Ideas',
    'umfrage.steps.planning': 'Planning',
    'umfrage.steps.gifts': 'Gift Subscriptions',
    'umfrage.gift.intro': 'As a thank you, you may provide 3 addresses. These people will receive the GLIDE magazine free for one year.',
    'umfrage.sender.title': 'Your Details (Freelancer / Sender)',
    'umfrage.gift.addresses': '3 Gift Addresses (Free Annual Subscription)',
    'umfrage.gift.address': 'Gift Address',
    'umfrage.gift.optional': 'Optional – only fill in if you want to provide a gift address',
    'umfrage.back': 'Back',
    'umfrage.next': 'Next',
    'umfrage.submit': 'Submit',
    'umfrage.submitting': 'Submitting…',
    'umfrage.success.title': 'Thank You!',
    'umfrage.success.desc': 'Thank you for your feedback and support of the cross-country skiing magazine!',
    'umfrage.success.sub': 'We appreciate your feedback and will take care of the free annual subscriptions.',
    'umfrage.fill_required': 'Please fill in all required fields',
    'umfrage.error': 'Submission error',
    'umfrage.answer.placeholder': 'Your answer…',
    'umfrage.other.placeholder': 'Please specify…',
    'umfrage.required_if': 'Required (if address is filled)',
    'umfrage.required': 'Required',
    'umfrage.invalid_email': 'Invalid email',
    // Survey questions
    'umfrage.q.satisfaction': 'How satisfied are you overall with the first three issues?',
    'umfrage.q.worked_well': 'What worked particularly well in your opinion?',
    'umfrage.q.improve': 'What should we definitely improve for future issues?',
    'umfrage.q.design': 'How do you rate the magazine design and layout?',
    'umfrage.q.design_feedback': 'Are there design aspects we should optimize? (e.g. imagery, readability, structure, article length)',
    'umfrage.q.collab': 'How did you experience the collaboration with the editorial team?',
    'umfrage.q.briefing': 'Were the briefings and topic guidelines clear enough?',
    'umfrage.q.planning_help': 'What would help you plan your work for future issues?',
    'umfrage.q.topics': 'Which topics would you like to work on? (e.g. athlete portraits, gear tests, training reports, destinations, youth, scene)',
    'umfrage.q.story_ideas': 'Do you have specific story ideas for upcoming issues?',
    'umfrage.q.missing': 'Which topics are generally missing in Swiss cross-country skiing journalism?',
    'umfrage.q.sections': 'Which sections would you like to write for or contribute to?',
    'umfrage.q.other_section': 'Other section',
    'umfrage.q.contribute': 'Would you like to contribute as a freelancer to future issues?',
    'umfrage.q.role': 'What role could you imagine?',
    'umfrage.q.other_role': 'Other role',
    'umfrage.q.count': 'How many contributions per season could you envision?',
    'umfrage.q.availability': 'Are there periods in winter when you are more or less available?',
    'umfrage.q.final': 'Any other ideas, feedback or wishes for the next issue?',
    'umfrage.q.promote_own': 'Would you actively promote the magazine through your channels (social media, network)?',
    'umfrage.q.promote_access': 'Would you actively promote the magazine through our channels if you had access?',
    // Survey options
    'umfrage.opt.very_satisfied': 'Very satisfied',
    'umfrage.opt.satisfied': 'Satisfied',
    'umfrage.opt.neutral': 'Neutral',
    'umfrage.opt.less_satisfied': 'Less satisfied',
    'umfrage.opt.unsatisfied': 'Unsatisfied',
    'umfrage.opt.very_good': 'Excellent',
    'umfrage.opt.good': 'Good',
    'umfrage.opt.needs_improvement': 'Needs improvement',
    'umfrage.opt.collab_very_good': 'Very good',
    'umfrage.opt.collab_good': 'Good',
    'umfrage.opt.yes_clear': 'Yes, very clear',
    'umfrage.opt.mostly_clear': 'Mostly clear',
    'umfrage.opt.partly_unclear': 'Partly unclear',
    'umfrage.opt.yes_gladly': 'Yes, gladly',
    'umfrage.opt.probably': 'Probably',
    'umfrage.opt.unsure': 'Unsure',
    'umfrage.opt.rather_not': 'Rather not',
    'umfrage.opt.author': 'Writer',
    'umfrage.opt.photographer': 'Photographer',
    'umfrage.opt.both': 'Both',
    'umfrage.opt.other_role': 'Other role',
    'umfrage.opt.1_contribution': '1 contribution',
    'umfrage.opt.2_3_contributions': '2–3 contributions',
    'umfrage.opt.4_plus': '4+ contributions',
    'umfrage.opt.yes': 'Yes',
    'umfrage.opt.partially': 'Partially',
    // Section options
    'umfrage.section.reportage': 'Feature Report',
    'umfrage.section.interview': 'Interview / Portrait',
    'umfrage.section.material': 'Gear / Technique',
    'umfrage.section.training': 'Training / Coaching',
    'umfrage.section.scene': 'Scene / Community',
    'umfrage.section.photo': 'Photo Report',
    'umfrage.section.other': 'Other',
    // Form labels
    'umfrage.form.firstname': 'First Name',
    'umfrage.form.lastname': 'Last Name',
    'umfrage.form.email': 'Email',
    'umfrage.form.phone': 'Phone',
    'umfrage.form.street': 'Street',
    'umfrage.form.zip': 'Zip Code',
    'umfrage.form.city': 'City',
    'umfrage.form.country': 'Country',
    'umfrage.form.company': 'Company',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('de');

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};